import { useMemo, useState } from 'react';
import { Plus, Loader2, Package, Pencil, Trash2, Sparkles, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/lib/error';
import { formatMoney } from '@/lib/money';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { SlideOver } from '@/components/ui/SlideOver';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import { useAuth } from '@/hooks/useAuth';
import {
  useRetailProducts,
  useCreateRetailProduct,
  useUpdateRetailProduct,
  useDeleteRetailProduct,
  useGenerateSku,
} from '@/hooks/api/useRetailProducts';
import { useRetailLookups } from '@/hooks/api/useRetailLookups';
import { useAllergens, useMenuSections } from '@/hooks/api/useRetailAttributes';
import type { ProductNutrientsPayload } from '@/services/api/retailProducts.api';
import { RetailLookupType, UserRole, type RetailProduct } from '@pms/shared';

// ─── Page ────────────────────────────────────────────────────────────────────

export function ProductsPage() {
  const propertyId = useSelectedPropertyId();
  const { user } = useAuth();
  const canEditCatalog =
    user?.role === UserRole.ADMIN || user?.role === UserRole.SUPER_ADMIN;

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<RetailProduct | null>(null);
  const [search, setSearch] = useState('');

  const listQuery = useRetailProducts({ propertyId, search: search.trim() || undefined });
  const deleteMutation = useDeleteRetailProduct();

  const allItems: RetailProduct[] = listQuery.data?.data ?? [];

  const items: RetailProduct[] = useMemo(() => {
    if (!search.trim()) return allItems;
    const q = search.toLowerCase();
    return allItems.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.sku.toLowerCase().includes(q) ||
        (p.upc ?? '').toLowerCase().includes(q),
    );
  }, [allItems, search]);

  // Lookups for resolving id → name in the table (active + inactive so historical
  // assignments still render a label).
  const departments = useRetailLookups({ type: RetailLookupType.DEPARTMENT, propertyId }, !!propertyId);
  const deptName = useMemo(() => {
    const map = new Map<string, string>();
    (departments.data?.data ?? []).forEach((d) => map.set(d.id, d.name));
    return map;
  }, [departments.data]);

  function handleDelete(item: RetailProduct) {
    if (!window.confirm('Delete this product?')) return;
    deleteMutation.mutate(item.id, {
      onError: (err) => {
        // Server soft-blocks when referenced by past sales (409 IN_USE).
        window.alert(extractErrorMessage(err));
      },
    });
  }

  return (
    <div>
      <PageHeader
        title="Products"
        description="Product catalog and pricing"
        actions={
          canEditCatalog ? (
            <Button
              onClick={() => { setEditing(null); setShowForm(true); }}
              disabled={!propertyId}
            >
              <Plus size={16} aria-hidden="true" />
              Add Product
            </Button>
          ) : undefined
        }
      />

      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by name, SKU or UPC..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-72"
          aria-label="Search products"
        />
      </div>

      {!propertyId ? (
        <EmptyState icon={Package} title="Select a property" description="Select a property to continue." />
      ) : listQuery.isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-primary-500" aria-label="Loading" />
        </div>
      ) : listQuery.isError ? (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(listQuery.error)}
        </p>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Package}
          title="No products"
          description="Create your first product."
          {...(canEditCatalog
            ? {
                actionLabel: 'Add Product',
                onAction: () => { setEditing(null); setShowForm(true); },
              }
            : {})}
        />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>SKU</Th>
                  <Th>Price</Th>
                  <Th>Department</Th>
                  <Th>Stock</Th>
                  <Th>Active</Th>
                  {canEditCatalog && <Th>Actions</Th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{p.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 font-mono">{p.sku}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{formatMoney(p.price)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{deptName.get(p.departmentId) ?? '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{p.stockOnHand ?? '—'}</td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                          p.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500',
                        )}
                      >
                        {p.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    {canEditCatalog && (
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button type="button" onClick={() => { setEditing(p); setShowForm(true); }} className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50" aria-label="Edit">
                            <Pencil size={14} />
                          </button>
                          <button type="button" onClick={() => handleDelete(p)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" aria-label="Delete">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {canEditCatalog && (
        <SlideOver
          isOpen={showForm}
          onClose={() => { setShowForm(false); setEditing(null); }}
          title={editing ? 'Edit Product' : 'Add Product'}
          width="max-w-xl"
        >
          <ProductForm
            propertyId={propertyId}
            editing={editing}
            onClose={() => { setShowForm(false); setEditing(null); }}
          />
        </SlideOver>
      )}
    </div>
  );
}

// ─── Form ────────────────────────────────────────────────────────────────────

function ProductForm({
  propertyId,
  editing,
  onClose,
}: {
  propertyId: string;
  editing: RetailProduct | null;
  onClose: () => void;
}) {
  const [name, setName] = useState(editing?.name ?? '');
  const [sku, setSku] = useState(editing?.sku ?? '');
  const [upc, setUpc] = useState(editing?.upc ?? '');
  const [price, setPrice] = useState(
    editing?.price != null ? parseFloat(editing.price).toFixed(2) : '',
  );
  const [departmentId, setDepartmentId] = useState(editing?.departmentId ?? '');
  const [categoryId, setCategoryId] = useState(editing?.categoryId ?? '');
  const [brandId, setBrandId] = useState(editing?.brandId ?? '');
  const [tags, setTags] = useState<string[]>(editing?.tags ?? []);
  const [allergenIds, setAllergenIds] = useState<string[]>(editing?.allergens ?? []);
  const [menuSectionId, setMenuSectionId] = useState(editing?.menuSectionId ?? '');
  const [calories, setCalories] = useState(
    editing?.nutrients?.calories != null ? String(editing.nutrients.calories) : '',
  );
  const [servingSize, setServingSize] = useState(editing?.nutrients?.servingSize ?? '');
  const [nutrientExtras, setNutrientExtras] = useState<{ label: string; value: string }[]>(
    editing?.nutrients?.extra ?? [],
  );
  const [taxable, setTaxable] = useState(editing?.taxable ?? false);
  const [isActive, setIsActive] = useState(editing?.isActive ?? true);
  const [upcError, setUpcError] = useState('');
  const [priceError, setPriceError] = useState('');

  const createMutation = useCreateRetailProduct();
  const updateMutation = useUpdateRetailProduct();
  const generateSku = useGenerateSku();
  const mutationError = createMutation.error ?? updateMutation.error;

  // Active-only lookups for the create dropdowns.
  const departments = useRetailLookups(
    { type: RetailLookupType.DEPARTMENT, propertyId, isActive: true },
    !!propertyId,
  );
  const categories = useRetailLookups(
    { type: RetailLookupType.CATEGORY, propertyId, isActive: true },
    !!propertyId,
  );
  const brands = useRetailLookups(
    { type: RetailLookupType.BRAND, propertyId, isActive: true },
    !!propertyId,
  );
  const tagOptions = useRetailLookups(
    { type: RetailLookupType.TAG, propertyId, isActive: true },
    !!propertyId,
  );
  const allergenOptions = useAllergens(
    { propertyId, isActive: true },
    !!propertyId,
  );
  const menuSections = useMenuSections(
    { propertyId, isActive: true },
    !!propertyId,
  );

  const priceNum = parseFloat(price);
  const isValid =
    name.trim().length > 0 &&
    sku.trim().length > 0 &&
    price.trim().length > 0 &&
    !Number.isNaN(priceNum) &&
    priceNum >= 0 &&
    departmentId.length > 0;

  function clearError() {
    if (createMutation.isError) createMutation.reset();
    if (updateMutation.isError) updateMutation.reset();
  }

  function handleGenerateSku() {
    clearError();
    generateSku.mutate(propertyId, {
      onSuccess: (res) => setSku(res.sku),
    });
  }

  function normalizePriceOnBlur() {
    if (!price.trim()) return;
    const n = parseFloat(price);
    if (Number.isNaN(n) || n < 0) {
      setPriceError('Enter a price of 0 or more.');
      return;
    }
    setPriceError('');
    setPrice(n.toFixed(2));
  }

  function validateUpcOnBlur() {
    if (!upc.trim()) {
      setUpcError('');
      return;
    }
    if (!/^\d{12,13}$/.test(upc.trim())) {
      setUpcError('UPC must be 12–13 digits.');
    } else {
      setUpcError('');
    }
  }

  function toggleTag(id: string) {
    setTags((prev) => (prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]));
  }

  function toggleAllergen(id: string) {
    setAllergenIds((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id],
    );
  }

  function addNutrientExtra() {
    setNutrientExtras((prev) => [...prev, { label: '', value: '' }]);
  }

  function updateNutrientExtra(idx: number, key: 'label' | 'value', val: string) {
    setNutrientExtras((prev) =>
      prev.map((row, i) => (i === idx ? { ...row, [key]: val } : row)),
    );
  }

  function removeNutrientExtra(idx: number) {
    setNutrientExtras((prev) => prev.filter((_, i) => i !== idx));
  }

  /** Assemble the optional nutrients payload; undefined when nothing is set. */
  function buildNutrients(): ProductNutrientsPayload | undefined {
    const cal = calories.trim() ? parseInt(calories, 10) : undefined;
    const extras = nutrientExtras
      .map((r) => ({ label: r.label.trim(), value: r.value.trim() }))
      .filter((r) => r.label && r.value);
    const hasAny =
      (cal != null && Number.isFinite(cal)) ||
      servingSize.trim().length > 0 ||
      extras.length > 0;
    if (!hasAny) return undefined;
    return {
      ...(cal != null && Number.isFinite(cal) ? { calories: cal } : {}),
      ...(servingSize.trim() ? { servingSize: servingSize.trim() } : {}),
      ...(extras.length ? { extra: extras } : {}),
    };
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid) return;

    if (upc.trim() && !/^\d{12,13}$/.test(upc.trim())) {
      setUpcError('UPC must be 12–13 digits.');
      return;
    }

    const n = parseFloat(price);
    if (Number.isNaN(n) || n < 0) {
      setPriceError('Enter a price of 0 or more.');
      return;
    }
    const parsedPrice = Number(n.toFixed(2));

    const nutrients = buildNutrients();

    if (editing) {
      updateMutation.mutate(
        {
          id: editing.id,
          payload: {
            name: name.trim(),
            sku: sku.trim(),
            upc: upc.trim() || undefined,
            price: parsedPrice,
            departmentId,
            categoryId: categoryId || undefined,
            brandId: brandId || undefined,
            tags,
            menuSectionId: menuSectionId || null,
            allergenIds,
            ...(nutrients ? { nutrients } : {}),
            taxable,
            isActive,
          },
        },
        { onSuccess: () => onClose() },
      );
    } else {
      createMutation.mutate(
        {
          propertyId,
          name: name.trim(),
          sku: sku.trim(),
          upc: upc.trim() || undefined,
          price: parsedPrice,
          departmentId,
          categoryId: categoryId || undefined,
          brandId: brandId || undefined,
          tags,
          menuSectionId: menuSectionId || null,
          allergenIds,
          ...(nutrients ? { nutrients } : {}),
          taxable,
          isActive,
        },
        { onSuccess: () => onClose() },
      );
    }
  }

  function handleDeactivate() {
    if (!editing) return;
    updateMutation.mutate(
      { id: editing.id, payload: { isActive: false } },
      { onSuccess: () => onClose() },
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="rp-name" className="block text-sm font-medium text-gray-700 mb-1">
          Name <span className="text-red-500">*</span>
        </label>
        <input id="rp-name" type="text" required value={name} onChange={(e) => { setName(e.target.value); clearError(); }} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      </div>

      <div>
        <label htmlFor="rp-sku" className="block text-sm font-medium text-gray-700 mb-1">
          SKU <span className="text-red-500">*</span>
        </label>
        <div className="flex gap-2">
          <input id="rp-sku" type="text" required value={sku} onChange={(e) => { setSku(e.target.value); clearError(); }} className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500" />
          <Button type="button" variant="secondary" onClick={handleGenerateSku} isLoading={generateSku.isPending}>
            <Sparkles size={14} aria-hidden="true" />
            Generate
          </Button>
        </div>
        {editing && (
          <p className="mt-1 text-xs text-gray-400">Editing the SKU does not rewrite historical sales.</p>
        )}
      </div>

      <div>
        <label htmlFor="rp-upc" className="block text-sm font-medium text-gray-700 mb-1">UPC</label>
        <input id="rp-upc" type="text" value={upc ?? ''} onChange={(e) => { setUpc(e.target.value); setUpcError(''); }} onBlur={validateUpcOnBlur} placeholder="Optional — 12–13 digits" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500" />
        {upcError && <p className="mt-1 text-xs text-red-600">{upcError}</p>}
      </div>

      <div>
        <label htmlFor="rp-price" className="block text-sm font-medium text-gray-700 mb-1">
          Price <span className="text-red-500">*</span>
        </label>
        <input id="rp-price" type="number" min="0" step="0.01" required value={price} onChange={(e) => { setPrice(e.target.value); setPriceError(''); }} onBlur={normalizePriceOnBlur} placeholder="0.00" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
        {priceError && <p className="mt-1 text-xs text-red-600">{priceError}</p>}
      </div>

      <div>
        <label htmlFor="rp-dept" className="block text-sm font-medium text-gray-700 mb-1">
          Department <span className="text-red-500">*</span>
        </label>
        <select id="rp-dept" required value={departmentId} onChange={(e) => { setDepartmentId(e.target.value); clearError(); }} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500">
          <option value="">Select a department…</option>
          {(departments.data?.data ?? []).map((d) => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="rp-cat" className="block text-sm font-medium text-gray-700 mb-1">Category</label>
        <select id="rp-cat" value={categoryId ?? ''} onChange={(e) => setCategoryId(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500">
          <option value="">None</option>
          {(categories.data?.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="rp-brand" className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
        <select id="rp-brand" value={brandId ?? ''} onChange={(e) => setBrandId(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500">
          <option value="">None</option>
          {(brands.data?.data ?? []).map((b) => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
      </div>

      <div>
        <span className="block text-sm font-medium text-gray-700 mb-1">Tags</span>
        {(tagOptions.data?.data ?? []).length === 0 ? (
          <p className="text-xs text-gray-400">No tags defined.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {(tagOptions.data?.data ?? []).map((t) => {
              const on = tags.includes(t.id);
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => toggleTag(t.id)}
                  aria-pressed={on}
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium border',
                    on
                      ? 'bg-primary-100 text-primary-700 border-primary-200'
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50',
                  )}
                >
                  {t.name}
                  {on && <X size={12} aria-hidden="true" />}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <span className="block text-sm font-medium text-gray-700 mb-1">Allergens</span>
        {(allergenOptions.data?.data ?? []).length === 0 ? (
          <p className="text-xs text-gray-400">No allergens defined.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {(allergenOptions.data?.data ?? []).map((a) => {
              const on = allergenIds.includes(a.id);
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => toggleAllergen(a.id)}
                  aria-pressed={on}
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium border',
                    on
                      ? 'bg-primary-100 text-primary-700 border-primary-200'
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50',
                  )}
                >
                  {a.name}
                  {on && <X size={12} aria-hidden="true" />}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <label htmlFor="rp-menu-section" className="block text-sm font-medium text-gray-700 mb-1">Menu section</label>
        <select id="rp-menu-section" value={menuSectionId ?? ''} onChange={(e) => setMenuSectionId(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500">
          <option value="">None</option>
          {(menuSections.data?.data ?? []).map((m) => (
            <option key={m.id} value={m.id}>{m.name}</option>
          ))}
        </select>
      </div>

      <div className="rounded-lg border border-gray-200 p-3 space-y-3">
        <span className="block text-sm font-medium text-gray-700">Nutrients</span>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="rp-calories" className="block text-xs font-medium text-gray-600 mb-1">Calories</label>
            <input id="rp-calories" type="number" min="0" step="1" value={calories} onChange={(e) => setCalories(e.target.value)} placeholder="—" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
          </div>
          <div>
            <label htmlFor="rp-serving" className="block text-xs font-medium text-gray-600 mb-1">Serving size</label>
            <input id="rp-serving" type="text" value={servingSize ?? ''} onChange={(e) => setServingSize(e.target.value)} placeholder="e.g. 1 cup" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
          </div>
        </div>
        {nutrientExtras.length > 0 && (
          <div className="space-y-2">
            {nutrientExtras.map((row, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <input
                  type="text"
                  value={row.label}
                  onChange={(e) => updateNutrientExtra(idx, 'label', e.target.value)}
                  placeholder="Label (e.g. Protein)"
                  aria-label={`Nutrient label ${idx + 1}`}
                  className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <input
                  type="text"
                  value={row.value}
                  onChange={(e) => updateNutrientExtra(idx, 'value', e.target.value)}
                  placeholder="Value (e.g. 12g)"
                  aria-label={`Nutrient value ${idx + 1}`}
                  className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <button type="button" onClick={() => removeNutrientExtra(idx)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" aria-label="Remove nutrient">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
        <button type="button" onClick={addNutrientExtra} className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700">
          <Plus size={13} aria-hidden="true" /> Add nutrient
        </button>
      </div>

      <div className="flex items-center gap-2">
        <input id="rp-taxable" type="checkbox" checked={taxable} onChange={(e) => setTaxable(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
        <label htmlFor="rp-taxable" className="text-sm font-medium text-gray-700">Taxable</label>
      </div>
      <div className="flex items-center gap-2">
        <input id="rp-active" type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
        <label htmlFor="rp-active" className="text-sm font-medium text-gray-700">Active</label>
      </div>

      {mutationError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(mutationError)}
        </p>
      )}

      <div className="flex items-center justify-between gap-3 pt-4 border-t border-gray-100">
        <div>
          {editing && editing.isActive && (
            <Button type="button" variant="secondary" onClick={handleDeactivate} isLoading={updateMutation.isPending}>
              Deactivate
            </Button>
          )}
        </div>
        <div className="flex gap-3">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" isLoading={createMutation.isPending || updateMutation.isPending} disabled={!isValid}>
            {editing ? 'Update' : 'Create'}
          </Button>
        </div>
      </div>
    </form>
  );
}

// ─── Shared ──────────────────────────────────────────────────────────────────

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
      {children}
    </th>
  );
}
